/**
 * Iter 57 — Email Delivery Diagnostic (2026-05-21)
 *
 * Admin-only page that answers the operator's 4 forensic questions
 * about outbound transactional email delivery without requiring them
 * to open the Resend dashboard or inspect logs manually:
 *
 *   1. Is RESEND_API_KEY actually populated in this env?
 *   2. Did the backend call Resend successfully?
 *   3. Does the send appear in Resend's records?
 *   4. What is the exact delivery status?
 *
 * Renders:
 *   - Env / config snapshot (key set / from-email / dry-run flag)
 *   - "Send test email" tool — dispatches a synthetic message and
 *     reveals send_result.mode + Resend live last_event + a
 *     human-readable interpretation.
 *   - Last 20 password-reset emails from audit log, each enriched
 *     with live Resend delivery state.
 */
import { useEffect, useState } from "react";
import { Envelope, ArrowsClockwise, PaperPlaneTilt, CheckCircle, Warning, Question, Info, Copy, ShieldCheck, Link as LinkIcon } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

const STATUS_THEME = {
    delivered:      { icon: CheckCircle, ring: "border-emerald-300 bg-emerald-50",  text: "text-emerald-900", chip: "bg-emerald-100 text-emerald-900" },
    bounced:        { icon: Warning,     ring: "border-rose-300 bg-rose-50",        text: "text-rose-900",    chip: "bg-rose-100 text-rose-900" },
    complained:     { icon: Warning,     ring: "border-rose-300 bg-rose-50",        text: "text-rose-900",    chip: "bg-rose-100 text-rose-900" },
    failed:         { icon: Warning,     ring: "border-rose-300 bg-rose-50",        text: "text-rose-900",    chip: "bg-rose-100 text-rose-900" },
    not_attempted:  { icon: Warning,     ring: "border-amber-300 bg-amber-50",      text: "text-amber-900",   chip: "bg-amber-100 text-amber-900" },
    sent_no_status: { icon: Info,        ring: "border-blue-300 bg-blue-50",        text: "text-blue-900",    chip: "bg-blue-100 text-blue-900" },
    queued:         { icon: Info,        ring: "border-blue-300 bg-blue-50",        text: "text-blue-900",    chip: "bg-blue-100 text-blue-900" },
    unknown:        { icon: Question,    ring: "border-slate-300 bg-slate-50",      text: "text-slate-900",   chip: "bg-slate-100 text-slate-900" },
};

const STATUS_LABEL = {
    delivered:      "DELIVERED",
    bounced:        "BOUNCED",
    complained:     "MARKED AS SPAM",
    failed:         "FAILED",
    not_attempted:  "NOT ATTEMPTED",
    sent_no_status: "SENT — STATUS UNAVAILABLE",
    queued:         "QUEUED",
    unknown:        "UNKNOWN",
};

function ConfigCard({ config }) {
    if (!config) return null;
    const ok = (cond, ifTrue, ifFalse) => cond
        ? <span className="text-emerald-700 font-semibold">{ifTrue}</span>
        : <span className="text-rose-700 font-semibold">{ifFalse}</span>;
    return (
        <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="diagnostic-config">
            <div className="reedos-eyebrow mb-3">Current environment</div>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
                <div className="flex justify-between"><dt className="text-slate-500">RESEND_API_KEY</dt><dd>{ok(config.resend_api_key_set, `set (${config.resend_api_key_prefix} · ${config.resend_api_key_length} chars)`, "NOT SET")}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Dry-run mode</dt><dd>{ok(!config.dry_run_mode, "off (real sends)", "ON (no actual emails)")}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500 shrink-0">RESEND_FROM_EMAIL</dt><dd className="font-mono text-right truncate">{config.resend_from_email || <span className="text-rose-700">(unset)</span>}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500 shrink-0">REEDOS_SUPPORT_EMAIL</dt><dd className="font-mono text-right truncate">{config.reedos_support_email || <span className="text-rose-700">(unset)</span>}</dd></div>
                <div className="flex justify-between gap-3 col-span-full"><dt className="text-slate-500 shrink-0">APP_PUBLIC_URL</dt><dd className="font-mono text-right truncate text-[11px]">{config.app_public_url || <span className="text-rose-700">(unset)</span>}</dd></div>
            </dl>
            {config.dry_run_mode && (
                <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900 flex gap-2">
                    <Warning size={14} weight="fill" className="shrink-0 mt-0.5 text-amber-700" />
                    <div>
                        <b>Dry-run mode is active.</b> Every send_email call returns <code>mode="dry_run"</code> and the reset link is only logged to backend logs. To actually dispatch emails, set <code>RESEND_API_KEY</code> in this environment and redeploy.
                    </div>
                </div>
            )}
        </div>
    );
}

function InterpretationBlock({ kind, interpretation, sendResult, liveStatus }) {
    if (!interpretation) return null;
    const theme = STATUS_THEME[interpretation.status] || STATUS_THEME.unknown;
    const Icon = theme.icon;
    return (
        <div className={`rounded-md border-2 p-4 ${theme.ring}`} data-testid={`interpretation-${interpretation.status}`}>
            <div className={`flex items-center gap-2 ${theme.text}`}>
                <Icon size={18} weight="fill" />
                <span className="text-[11px] tracking-wide font-bold uppercase">{STATUS_LABEL[interpretation.status] || interpretation.status}</span>
            </div>
            <p className={`mt-2 text-[13px] ${theme.text}`}>{interpretation.summary}</p>
            {interpretation.fix_hint && (
                <p className={`mt-2 text-[11px] ${theme.text} opacity-90`}>
                    <b>Suggested action:</b> {interpretation.fix_hint}
                </p>
            )}
            <details className="mt-3 text-[11px]">
                <summary className={`cursor-pointer ${theme.text} opacity-80`}>Raw response</summary>
                <pre className="mt-2 bg-white/60 p-2 rounded border border-slate-200 text-[10px] overflow-x-auto whitespace-pre-wrap break-all font-mono">{JSON.stringify({ send_result: sendResult, live_status: liveStatus }, null, 2)}</pre>
            </details>
        </div>
    );
}

function RecentSendsTable({ rows }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="text-[12px] text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-md">
                No password-reset emails have been dispatched yet.
            </div>
        );
    }
    return (
        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto" data-testid="diagnostic-recent-sends">
            <table className="min-w-full text-[11px]">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
                    <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Recipient</th>
                        <th className="px-3 py-2 text-left">Backend mode</th>
                        <th className="px-3 py-2 text-left">Resend last_event</th>
                        <th className="px-3 py-2 text-left">Message ID</th>
                        <th className="px-3 py-2 text-left">IP</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {rows.map((r, i) => {
                        const live = r.live_status || {};
                        const event = (live.last_event || "").toLowerCase();
                        const eventColor = {
                            delivered: "bg-emerald-100 text-emerald-800",
                            bounced: "bg-rose-100 text-rose-800",
                            complained: "bg-rose-100 text-rose-800",
                            sent: "bg-blue-100 text-blue-800",
                            queued: "bg-blue-100 text-blue-800",
                        }[event] || "bg-slate-100 text-slate-600";
                        return (
                            <tr key={i} className="hover:bg-slate-50">
                                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{(r.created_at || "").replace("T", " ").slice(0, 19)}</td>
                                <td className="px-3 py-2 font-mono text-slate-900">{r.actor_email}</td>
                                <td className="px-3 py-2">
                                    <span className={`inline-flex px-1.5 py-0.5 rounded font-mono text-[10px] ${r.mode === "sent" ? "bg-emerald-100 text-emerald-800" : r.mode === "dry_run" ? "bg-amber-100 text-amber-800" : r.mode === "failed" ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-600"}`}>
                                        {r.mode || "?"}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    {live.available ? (
                                        <span className={`inline-flex px-1.5 py-0.5 rounded font-mono text-[10px] ${eventColor}`}>{event || "—"}</span>
                                    ) : (
                                        <span className="text-[10px] text-slate-400 italic" title={live.reason}>n/a</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 font-mono text-[10px] text-slate-500 max-w-[260px] truncate" title={r.provider_message_id}>{r.provider_message_id || "—"}</td>
                                <td className="px-3 py-2 text-slate-500 font-mono text-[10px]">{r.ip || "—"}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default function EmailDiagnostic() {
    const [config, setConfig] = useState(null);
    const [recentSends, setRecentSends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [testTo, setTestTo] = useState("");
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    // Iter 57.1 — Out-of-band reset link generator state
    const [genEmail, setGenEmail] = useState("");
    const [generating, setGenerating] = useState(false);
    const [genResult, setGenResult] = useState(null);
    const [copied, setCopied] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/email-diagnostic");
            setConfig(data.config);
            setRecentSends(data.recent_sends || []);
        } catch (err) {
            toast.error(formatApiError(err, "Could not load diagnostic"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const sendTest = async () => {
        if (!testTo.trim() || !testTo.includes("@")) {
            toast.error("Enter a valid recipient email");
            return;
        }
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/admin/email-diagnostic/test-send", { to: testTo.trim() });
            setTestResult(data);
            setConfig(data.config);
            // Reload audit history so the new entry shows up
            setTimeout(refresh, 800);
        } catch (err) {
            toast.error(formatApiError(err, "Diagnostic send failed"));
        } finally {
            setTesting(false);
        }
    };

    // Iter 57.1 — Out-of-band reset link generator.
    const generateLink = async () => {
        if (!genEmail.trim() || !genEmail.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setGenerating(true);
        setGenResult(null);
        setCopied(false);
        try {
            const { data } = await api.post("/admin/email-diagnostic/generate-reset-link", { email: genEmail.trim() });
            setGenResult(data);
        } catch (err) {
            toast.error(formatApiError(err, "Could not generate reset link"));
        } finally {
            setGenerating(false);
        }
    };

    const copyLink = async () => {
        if (!genResult?.reset_url) return;
        try {
            await navigator.clipboard.writeText(genResult.reset_url);
            setCopied(true);
            toast.success("Reset link copied to clipboard");
            setTimeout(() => setCopied(false), 3000);
        } catch (err) {
            toast.error("Could not copy — select & copy manually");
        }
    };

    return (
        <div className="space-y-6" data-testid="email-diagnostic-page">
            <PageHeader
                eyebrow="Operations · Admin only"
                title="Email delivery diagnostic"
                description="Inspect outbound transactional email health. Combines the current Resend env config, the last 20 password-reset sends from the audit log, and live delivery state fetched from Resend's API."
                icon={Envelope}
                actions={
                    <Button onClick={refresh} variant="outline" size="sm" data-testid="diagnostic-refresh-btn">
                        <ArrowsClockwise size={14} weight="bold" className="mr-1" /> Refresh
                    </Button>
                }
            />

            {loading && !config ? (
                <div className="text-[12px] text-slate-500 text-center py-8">Loading diagnostic snapshot…</div>
            ) : (
                <>
                    <ConfigCard config={config} />

                    {/* Test send */}
                    <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="diagnostic-test-send">
                        <div className="reedos-eyebrow mb-2">Send diagnostic test</div>
                        <p className="text-[12px] text-slate-600 mb-3">
                            Dispatches one synthetic email through the same path the password-reset flow uses. The raw send result and Resend's live delivery state are shown below — bypassing the public anti-enumeration response.
                        </p>
                        <div className="flex gap-2 items-end">
                            <div className="flex-1">
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase">Recipient</Label>
                                <Input
                                    type="email"
                                    value={testTo}
                                    onChange={(e) => setTestTo(e.target.value)}
                                    placeholder="rahulgalu@gmail.com"
                                    className="mt-1 h-9 font-mono text-[12px]"
                                    data-testid="diagnostic-test-to"
                                />
                            </div>
                            <Button
                                onClick={sendTest}
                                disabled={testing || !testTo.trim()}
                                className="h-9 gap-1.5"
                                data-testid="diagnostic-test-submit"
                            >
                                <PaperPlaneTilt size={13} weight="bold" />
                                {testing ? "Sending…" : "Send test"}
                            </Button>
                        </div>
                        {testResult && (
                            <div className="mt-4">
                                <InterpretationBlock
                                    interpretation={testResult.interpretation}
                                    sendResult={testResult.send_result}
                                    liveStatus={testResult.live_status}
                                />
                            </div>
                        )}
                    </div>

                    {/* Iter 57.1 — Out-of-band reset link generator */}
                    <div className="rounded-md border-2 border-indigo-200 bg-indigo-50/40 p-4" data-testid="diagnostic-out-of-band">
                        <div className="flex items-baseline justify-between mb-2">
                            <div className="reedos-eyebrow text-indigo-800">Out-of-band reset link · email-bypass</div>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">Admin only</span>
                        </div>
                        <p className="text-[12px] text-indigo-900/90 mb-3">
                            When Resend or recipient-mailbox filtering is misbehaving, mint a valid reset link directly here and hand-deliver it via WhatsApp / SMS / voice. The link is functionally identical to one sent via email — single-use, 1-hour TTL, sha256-hashed in DB, fully audited, and successful use signs out every existing session of the target user.
                        </p>
                        <div className="flex gap-2 items-end">
                            <div className="flex-1">
                                <Label className="text-[10px] font-semibold text-indigo-900 uppercase">Target user email</Label>
                                <Input
                                    type="email"
                                    value={genEmail}
                                    onChange={(e) => setGenEmail(e.target.value)}
                                    placeholder="rahulgalu@gmail.com"
                                    className="mt-1 h-9 font-mono text-[12px] bg-white"
                                    data-testid="diagnostic-gen-email"
                                />
                            </div>
                            <Button
                                onClick={generateLink}
                                disabled={generating || !genEmail.trim()}
                                className="h-9 gap-1.5 bg-indigo-700 hover:bg-indigo-800"
                                data-testid="diagnostic-gen-submit"
                            >
                                <LinkIcon size={13} weight="bold" />
                                {generating ? "Generating…" : "Generate reset link"}
                            </Button>
                        </div>

                        {genResult && (
                            <div className="mt-4 space-y-3" data-testid="diagnostic-gen-result">
                                <div className="rounded border-2 border-emerald-300 bg-emerald-50 p-3">
                                    <div className="flex items-center gap-1.5 text-emerald-900 text-[11px] font-bold uppercase tracking-wide">
                                        <CheckCircle size={14} weight="fill" /> Reset link ready
                                    </div>
                                    <div className="mt-2 text-[12px] text-emerald-900">
                                        For <b>{genResult.target.email}</b> ({genResult.target.name || genResult.target.role}) · expires at <span className="font-mono">{(genResult.expires_at || "").slice(0, 19).replace("T", " ")}</span> UTC · TTL <b>{genResult.ttl_minutes} min</b>
                                    </div>
                                </div>
                                <div className="rounded border border-slate-300 bg-white p-3">
                                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Reset URL · copy & deliver out-of-band</Label>
                                    <div className="flex gap-2 mt-1.5">
                                        <Input
                                            value={genResult.reset_url}
                                            readOnly
                                            onFocus={(e) => e.target.select()}
                                            className="h-9 font-mono text-[11px] bg-slate-50"
                                            data-testid="diagnostic-gen-url"
                                        />
                                        <Button
                                            onClick={copyLink}
                                            variant="outline"
                                            className={`h-9 gap-1.5 shrink-0 ${copied ? "border-emerald-400 text-emerald-700" : ""}`}
                                            data-testid="diagnostic-gen-copy"
                                        >
                                            <Copy size={13} weight="bold" /> {copied ? "Copied ✓" : "Copy"}
                                        </Button>
                                    </div>
                                </div>
                                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900 flex gap-2">
                                    <Warning size={14} weight="fill" className="shrink-0 mt-0.5 text-amber-700" />
                                    <div>
                                        <b>Treat this link like a password.</b> {genResult.warning}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Recent sends */}
                    <div>
                        <div className="flex items-end justify-between mb-2">
                            <div>
                                <div className="reedos-eyebrow">Recent password-reset emails</div>
                                <div className="text-[11px] text-slate-500">Last 20 entries from <code className="font-mono">audit_logs</code> · enriched with live Resend status.</div>
                            </div>
                            <ShieldCheck size={16} weight="duotone" className="text-slate-400" />
                        </div>
                        <RecentSendsTable rows={recentSends} />
                    </div>
                </>
            )}
        </div>
    );
}
